import { ImageWithLoading } from "../../../components/ImageWithLoading";
import { mergeClass } from "../../../utils";
import {
  Border,
  DivWFHF,
  IPreviewProps,
  WaveImage,
  getTextSlideData,
} from "./utils";
import parse from "html-react-parser";

const LandscapeImage = ({ itemData }: IPreviewProps) => {
  const { bgColor } = getTextSlideData(itemData);
  return (
    <DivWFHF className="grid flex" style={{ backgroundColor: bgColor }}>
      {itemData && (itemData.videoThumbnail || itemData.imageUrl) ? (
        <img
          className="w-full h-full object-cover"
          src={itemData.videoThumbnail || itemData.imageUrl}
        />
      ) : (
        <div className="w-full text-center place-self-center text-darkGrayBg">
          Upload image / video for preview
        </div>
      )}
    </DivWFHF>
  );
};

const LandscapeText = ({
  itemData,
  large = false,
}: IPreviewProps & { large?: boolean }) => {
  const { bgColor, pictogram, darkBg } = getTextSlideData(itemData);
  return (
    <DivWFHF
      className={mergeClass("relative", {
        "text-white": darkBg,
      })}
      style={{ backgroundColor: bgColor }}
    >
      <div
        className={mergeClass(
          "absolute h-full overflow-hidden font-bold",
          "top-[2.82rem] left-0 pl-[1.77rem] pr-[6.5rem]",
          {
            "text-[1.1rem] leading-[1.25rem]": !large,
            "text-[1.117rem] leading-[1.25rem] ": large,
          }
        )}
      >
        {itemData.message || (
          <span className="font-thin text-darkGrayBg">Enter a message</span>
        )}
      </div>
      <div className="absolute bottom-0 right-0">
        {pictogram && (
          <img
            src={`/assets/pictograms/${pictogram}`}
            className={mergeClass("w-[7rem] h-[7rem] filter", {
              "brightness-0": !darkBg,
            })}
          />
        )}
      </div>
    </DivWFHF>
  );
};

const LandscapeLargeText = (props: IPreviewProps) => (
  <LandscapeText {...props} large />
);

const LandscapeTextAndImage = ({
  itemData,
  reverse,
  large = false,
}: IPreviewProps & { reverse: boolean; large?: boolean }) => {
  const { bgColor, hasTitle, darkBg } = getTextSlideData(itemData);
  return (
    <div
      className="w-full h-full grid grid-cols-2"
      style={{ backgroundColor: bgColor }}
    >
      <div
        className={mergeClass(
          "overflow-hidden relative",
          {
            "pl-7 pr-[0.9rem] pt-[3.1rem] pb-3": !large,
            "pl-7 pr-[1.01rem] pt-[3.1rem] pb-3": large,
          },
          {
            "order-last": reverse,
            "text-white": darkBg,
          }
        )}
      >
        <WaveImage darkBg={darkBg} className="w-[4rem] top-[1.15rem]" />
        {hasTitle && (
          <div
            className={mergeClass("mb-1 font-bold", {
              "text-[0.96rem] leading-[1.1rem]": !large,
              "text-[0.96rem] leading-[1.1rem] ": large,
            })}
          >
            {itemData.title}
          </div>
        )}
        {itemData.subheading && itemData.subheading.length > 0 && (
          <div className="text-[0.642rem] mt-[0.4rem] text-black">
            {itemData.subheading}
          </div>
        )}
        {itemData.caption && itemData.caption.length > 0 ? (
          <div
            className={mergeClass(
              {
                "text-[0.486rem] pt-[0.4rem]": !large,
                "text-[0.48rem] pt-[0.42rem]": large,
              },
              "leading-[0.62rem] pr-2"
            )}
          >
            {parse(itemData.caption)}
          </div>
        ) : (
          <span className="font-thin text-lg text-darkGrayBg">
            Add a caption
          </span>
        )}
      </div>
      {itemData && (itemData.videoThumbnail || itemData.imageUrl) ? (
        <div className="h-full overflow-hidden">
          <ImageWithLoading
            className="w-full h-full object-cover"
            src={itemData.videoThumbnail || itemData.imageUrl}
          />
        </div>
      ) : (
        <div className="w-full h-full grid content-center text-center text-darkGrayBg bg-white/30">
          Upload image / video for preview
        </div>
      )}
    </div>
  );
};

const LandscapeTextAndImageForward = (props: IPreviewProps) => (
  <LandscapeTextAndImage {...props} reverse={false} />
);

const LandscapeTextAndImageReverse = (props: IPreviewProps) => (
  <LandscapeTextAndImage {...props} reverse={true} />
);

const LandscapeLargeTextAndImageForward = (props: IPreviewProps) => (
  <LandscapeTextAndImage {...props} reverse={false} large />
);

const LandscapeLargeTextAndImageReverse = (props: IPreviewProps) => (
  <LandscapeTextAndImage {...props} reverse={true} large />
);

const DonorPreview = ({ itemData }: IPreviewProps) => {
  const { darkBg, bgColor } = getTextSlideData(itemData);
  const donors = itemData?.donorList || [];
  return (
    <DivWFHF
      className={mergeClass("pl-6 pr-2 pt-4 pb-2 relative flex", {
        "text-white": darkBg,
      })}
      style={{ backgroundColor: bgColor }}
    >
      <WaveImage darkBg={darkBg} className="w-[4rem] top-[1rem]" />
      <div className="w-2/5 py-4 flex flex-col gap-3 pr-4">
        <div className="font-bold text-3xl">Donors</div>
        <div className="font-bold text-[0.96rem] leading-[1.2rem]">
          {itemData.subheading}
        </div>
        <div className="text-[0.486rem] leading-[0.62rem]">
          {itemData.caption && parse(itemData.caption)}
        </div>
      </div>
      <div className="w-3/5 grid grid-cols-2 text-[0.45rem] font-bold gap-x-2 gap-y-1 pb-4">
        {donors &&
          donors.length > 0 &&
          donors.map((d: string, index: number) => (
            <div className="grid content-center" key={index}>
              {d.replaceAll('"', "")}
            </div>
          ))}
      </div>
    </DivWFHF>
  );
};

const LandscapePreviews = [
  LandscapeImage,
  LandscapeImage, // to change to video
  LandscapeText,
  LandscapeTextAndImageForward,
  LandscapeTextAndImageReverse,
  LandscapeTextAndImageForward,
  LandscapeTextAndImageReverse,
];

const LandscapeLargePreviews = [
  LandscapeImage,
  LandscapeImage,
  LandscapeLargeText,
  LandscapeLargeTextAndImageForward,
  LandscapeLargeTextAndImageReverse,
  LandscapeLargeTextAndImageForward,
  LandscapeLargeTextAndImageReverse,
];

const DonorPreviews = [...LandscapePreviews, DonorPreview];

const BaseLandscapePreviews = ({
  itemData,
  templateList,
}: IPreviewProps & {
  templateList: any[];
}) => {
  const Preview = templateList[itemData.templateType] || null;
  return (
    <div
      className={mergeClass(
        "relative w-[400px] h-[225px] space-y-4",
        "border border-gray-500 text-left",
        {
          "h-auto": Preview === DonorPreview,
        }
      )}
    >
      {Preview ? (
        <Preview itemData={itemData} />
      ) : (
        <DivWFHF className="text-center grid content-center text-darkGrayBg">
          Preview unavailable
        </DivWFHF>
      )}
    </div>
  );
};

const PreviewLandscape = (props: IPreviewProps) => {
  return <BaseLandscapePreviews {...props} templateList={LandscapePreviews} />;
};

const PreviewLandscapeLarge = (props: IPreviewProps) => {
  return (
    <BaseLandscapePreviews {...props} templateList={LandscapeLargePreviews} />
  );
};

const PreviewDonorLandscape = (props: IPreviewProps) => {
  return <BaseLandscapePreviews {...props} templateList={DonorPreviews} />;
};

export { PreviewLandscape, PreviewDonorLandscape, PreviewLandscapeLarge };
